// FRT Customers list page — table with status filter, account_count column,
// and quick edit/suppress actions for A/S/admin.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { customers, lookups } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import { PLATFORM_NAME_MAX } from '@frt/shared-types';
import type { ProfileMode, StatusActiveSuppressed } from '@frt/shared-types';
import { ModeSelect } from './platforms-card';

/** A platform row on the New Customer form (mig 0017; named since 0018). */
interface PlatformDraft {
  key: number;
  platform_id: number;
  name: string;
  profile_mode: ProfileMode;
}
let draftSeq = 0;
const newPlatformDraft = (): PlatformDraft => ({
  key: ++draftSeq,
  platform_id: 0,
  name: '',
  profile_mode: 'per_tester',
});

export function CustomersListPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusActiveSuppressed | ''>('');
  // mig 0014 — FRT customers vs non-customer FIs added for linked accounts
  const [kind, setKind] = useState<'' | 'customer' | 'non_customer'>('');
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  // mig 0017 — platforms offered; a type may appear more than once under
  // different names (mig 0018)
  const [platforms, setPlatforms] = useState<PlatformDraft[]>(() => [newPlatformDraft()]);
  const [showPlatformError, setShowPlatformError] = useState(false);
  const { data: platformOptions } = useQuery({
    queryKey: ['lookups', 'platform-options'],
    queryFn: () => lookups.platformOptions.list(),
    enabled: creating,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', { status, kind, per_page: 200 }],
    queryFn: () =>
      customers.list({
        per_page: 200,
        ...(status ? { status: status as StatusActiveSuppressed } : {}),
        ...(kind ? { is_customer: kind === 'customer' } : {}),
      }),
  });

  const create = useMutation({
    // Category defaults server-side to "Active Customers"; admin can change
    // it on the customer detail page after creation.
    mutationFn: () =>
      customers.create({
        code: code.trim(),
        name: name.trim(),
        platforms: chosenPlatforms.map((p) => ({
          platform_id: p.platform_id,
          name: p.name.trim() || null,
          profile_mode: p.profile_mode,
        })),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      void queryClient.invalidateQueries({ queryKey: ['customer-platforms'] });
      setCreating(false);
      setCode('');
      setName('');
      setPlatforms([newPlatformDraft()]);
      setShowPlatformError(false);
    },
  });

  const canEdit = me?.role === 'admin' || me?.role === 'senior';
  const canSuppress = me?.role === 'admin';

  const platformTypes = (platformOptions?.data ?? []).filter((o) => o.active);
  const chosenPlatforms = platforms.filter((p) => p.platform_id > 0);
  // Names default to the type; they must differ within the customer.
  const effectiveNames = chosenPlatforms.map((p) =>
    (p.name.trim() || platformTypes.find((t) => t.id === p.platform_id)?.name || '').toLowerCase(),
  );
  const duplicateNames = new Set(effectiveNames).size !== effectiveNames.length;
  const updateDraft = (key: number, patch: Partial<PlatformDraft>) =>
    setPlatforms(platforms.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">FRT Customers</h1>
        {canEdit && (
          <Button onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'New Customer'}
          </Button>
        )}
      </div>
      {creating && (
        <Card>
          <CardHeader>
            <CardTitle>Create FRT Customer</CardTitle>
          </CardHeader>
          <CardBody>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                setShowPlatformError(true);
                if (!code.trim() || !name.trim() || chosenPlatforms.length === 0 || duplicateNames) return;
                create.mutate();
              }}
            >
              <div className="flex flex-wrap gap-3 items-end">
                <label className="block text-sm w-40">
                  <span className="block text-slate-600">Code *</span>
                  <Input value={code} onChange={(e) => setCode(e.target.value)} required />
                </label>
                <label className="block text-sm flex-1 min-w-[16rem]">
                  <span className="block text-slate-600">Name *</span>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required />
                </label>
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm text-slate-600">
                  Platforms * <span className="text-xs text-slate-500">(at least one; choose how its online profiles work)</span>
                </legend>
                {platforms.map((d) => {
                  const typeName = platformTypes.find((t) => t.id === d.platform_id)?.name;
                  return (
                    <div key={d.key} className="flex flex-wrap items-center gap-2 text-sm">
                      <select
                        value={d.platform_id}
                        onChange={(e) => updateDraft(d.key, { platform_id: Number(e.target.value) })}
                        className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                        aria-label="Platform type"
                      >
                        <option value={0}>— type —</option>
                        {platformTypes.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                      <Input
                        value={d.name}
                        maxLength={PLATFORM_NAME_MAX}
                        placeholder={typeName ? `Name (default: ${typeName})` : 'Name'}
                        onChange={(e) => updateDraft(d.key, { name: e.target.value })}
                        className="w-56"
                        aria-label="Platform name"
                      />
                      <ModeSelect
                        value={d.profile_mode}
                        onChange={(m) => updateDraft(d.key, { profile_mode: m })}
                        compact
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={platforms.length <= 1}
                        onClick={() => setPlatforms(platforms.filter((p) => p.key !== d.key))}
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setPlatforms([...platforms, newPlatformDraft()])}
                >
                  + Add platform
                </Button>
                <p className="text-xs text-slate-500">
                  The same type can be added more than once under different names, e.g. Personal as
                  “Consumer Deposits” and “Consumer Lending”. Types are managed under Lookups →
                  Platform Types.
                </p>
                {showPlatformError && chosenPlatforms.length === 0 && (
                  <p className="text-xs text-red-600">Add at least one platform.</p>
                )}
                {duplicateNames && (
                  <p className="text-xs text-red-600">Give each platform a different name.</p>
                )}
              </fieldset>
              <Button type="submit" disabled={create.isPending}>
                Create
              </Button>
            </form>
            <p className="mt-2 text-xs text-slate-500">
              New customers default to the <span className="font-medium">Active Customers</span> category.
              Category and platforms can be changed on the customer detail page after creation.
            </p>
            {create.error && (
              <div className="mt-2 text-sm text-red-600">
                {(create.error as Error).message}
              </div>
            )}
          </CardBody>
        </Card>
      )}
      <div className="flex gap-2 items-center text-sm">
        <label>
          <span className="mr-2 text-slate-600">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusActiveSuppressed | '')}
            className="rounded border border-slate-300 px-2 py-1"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="suppressed">Suppressed</option>
          </select>
        </label>
        <label>
          <span className="mr-2 text-slate-600">Type</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as '' | 'customer' | 'non_customer')}
            className="rounded border border-slate-300 px-2 py-1"
          >
            <option value="">All</option>
            <option value="customer">FRT customers</option>
            <option value="non_customer">Non-customer FIs</option>
          </select>
        </label>
      </div>
      <Card>
        <CardBody>
          {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
          {isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-1 pr-3">Code</th>
                  <th className="py-1 pr-3">Name</th>
                  <th className="py-1 pr-3">Category</th>
                  <th className="py-1 pr-3">Accounts</th>
                  <th className="py-1 pr-3">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(data?.data ?? []).map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="py-1 pr-3 font-mono">{row.code}</td>
                    <td className="py-1 pr-3">
                      <Link
                        to="/customers/$id"
                        params={{ id: String(row.id) }}
                        className="text-brand-700 hover:underline"
                      >
                        {row.name}
                      </Link>
                    </td>
                    <td className="py-1 pr-3">{row.frt_category_name ?? '—'}</td>
                    <td className="py-1 pr-3">{row.account_count}</td>
                    <td className="py-1 pr-3">{row.status}</td>
                    <td className="py-1 text-right">
                      {canSuppress && (
                        <SuppressButton id={row.id} status={row.status} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function SuppressButton({ id, status }: { id: number; status: StatusActiveSuppressed }) {
  const queryClient = useQueryClient();
  const action = useMutation({
    mutationFn: () =>
      status === 'active' ? customers.suppress(id) : customers.restore(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
  return (
    <Button
      size="sm"
      variant={status === 'active' ? 'destructive' : 'secondary'}
      onClick={() => action.mutate()}
      disabled={action.isPending}
    >
      {status === 'active' ? 'Suppress' : 'Restore'}
    </Button>
  );
}
